// Phase 24D — Source Performance widget for Order Report
import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";

const SOURCES = [
  { key: "field",        label: "Field",       color: "text-emerald-700 bg-emerald-50 border-emerald-200" },
  { key: "distributor",  label: "Distributor", color: "text-orange-700 bg-orange-50 border-orange-200" },
  { key: "admin",        label: "Admin",       color: "text-purple-700 bg-purple-50 border-purple-200" },
];

export default function SourcePerfWidget({ filters }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!filters?.date_from || !filters?.date_to) return;
    const params = { date_from: filters.date_from, date_to: filters.date_to };
    if (filters.zone_id) params.zone_id = filters.zone_id;
    if (filters.sub_zone_id) params.sub_zone_id = filters.sub_zone_id;
    api.get("/reports/orders/source-performance", { params })
       .then(r => setData(r.data)).catch(() => {});
  }, [filters?.date_from, filters?.date_to, filters?.zone_id, filters?.sub_zone_id]);

  if (!data) return null;
  return (
    <Card className="border-neutral-200 rounded-2xl" data-testid="source-perf-widget">
      <CardContent className="p-3">
        <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-2">
          Order Source Performance · WoW
        </div>
        <div className="grid grid-cols-3 gap-3">
          {SOURCES.map(s => {
            const cur = data.current[s.key] || { count: 0, kg: 0 };
            const wow = data.wow_delta[s.key] || { count_pct: 0, kg_pct: 0 };
            return (
              <div key={s.key} className={`rounded-xl border p-2 ${s.color}`}
                   data-testid={`perf-tile-${s.key}`}>
                <div className="text-xs font-semibold">{s.label}</div>
                <div className="text-xl font-bold">{cur.count}</div>
                <div className="text-xs">{Number(cur.kg).toLocaleString("en-IN", { maximumFractionDigits: 1 })} kg</div>
                <DeltaChip pct={wow.kg_pct} />
              </div>
            );
          })}
        </div>
        {(data.current.legacy?.count || 0) > 0 && (
          <div className="mt-2 text-[11px] text-neutral-500 flex items-center gap-1"
               data-testid="perf-legacy-chip">
            <span className="inline-block bg-neutral-100 border border-neutral-200 rounded-full px-2 py-0.5">
              Legacy (unknown): {data.current.legacy.count} orders · {Number(data.current.legacy.kg).toLocaleString("en-IN", { maximumFractionDigits: 1 })} kg
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function DeltaChip({ pct }) {
  const n = Number(pct) || 0;
  const Icon = n > 0 ? TrendingUp : n < 0 ? TrendingDown : Minus;
  const cls = n > 0 ? "text-emerald-600" : n < 0 ? "text-red-600" : "text-neutral-400";
  return (
    <span className={`text-[11px] font-medium inline-flex items-center gap-0.5 ${cls}`}>
      <Icon className="w-3 h-3" /> {n > 0 ? "+" : ""}{n.toFixed(1)}%
    </span>
  );
}
